import { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { PageLayout } from '@/components/layout/PageLayout';
import { cn } from '@/lib/utils';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const GET_TEST_BY_SLUG = gql`
  query GetTestBySlug($slug: String!) {
    getTestBySlug(slug: $slug) {
      testId
      name
    }
  }
`;

const SELF_REGISTER_CANDIDATE = gql`
  mutation SelfRegisterCandidate($input: SelfRegisterInput!) {
    selfRegisterCandidate(input: $input) {
      success
      scheduleId
      candidateId
      message
    }
  }
`;

type GitHubVerifyStatus = 'idle' | 'loading' | 'valid' | 'invalid';

export function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const email = searchParams.get('email') || '';
  const testSlug = searchParams.get('test') || '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [githubId, setGithubId] = useState('');
  const [githubStatus, setGithubStatus] = useState<GitHubVerifyStatus>('idle');
  const [githubAvatarUrl, setGithubAvatarUrl] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: testData, loading: testLoading, error: testError } = useQuery(GET_TEST_BY_SLUG, {
    variables: { slug: testSlug },
    skip: !testSlug,
  });

  const [selfRegister, { loading: registerLoading }] = useMutation(SELF_REGISTER_CANDIDATE);

  const verifyGithubId = useCallback(async (username: string) => {
    if (!username.trim()) {
      setGithubStatus('idle');
      setGithubAvatarUrl(null);
      return;
    }

    setGithubStatus('loading');
    setGithubAvatarUrl(null);

    try {
      const response = await fetch(`https://api.github.com/users/${encodeURIComponent(username.trim())}`);
      if (response.ok) {
        const userData = await response.json();
        setGithubStatus('valid');
        setGithubAvatarUrl(userData.avatar_url || null);
      } else {
        setGithubStatus('invalid');
      }
    } catch {
      setGithubStatus('invalid');
    }
  }, []);

  const handleGithubBlur = () => {
    verifyGithubId(githubId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !testSlug) {
      setFormError('Missing required URL parameters (email and test).');
      return;
    }

    if (!firstName.trim() || !lastName.trim() || !githubId.trim()) {
      setFormError('All fields are required.');
      return;
    }

    try {
      const result = await selfRegister({
        variables: {
          input: {
            email,
            testSlug,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            githubId: githubId.trim(),
          },
        },
      });

      const registration = result.data?.selfRegisterCandidate;

      if (registration?.success) {
        const params = new URLSearchParams();
        if (registration.scheduleId) {
          params.set('scheduleId', registration.scheduleId);
        }
        navigate(`/success?${params.toString()}`);
      } else {
        setFormError(registration?.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
    }
  };

  const test = testData?.getTestBySlug;
  const testNotFound = !testLoading && !testError && !test && !!testSlug;

  if (!email || !testSlug) {
    return (
      <PageLayout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Invalid Registration Link</h2>
          <p className="text-muted-foreground">
            This registration link is missing required parameters. Please use the link provided in your invitation email.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Candidate Registration</h2>
          <p className="text-muted-foreground mt-1">
            Complete your registration to begin the technical assessment process.
          </p>
        </div>

        {testLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading test information...</span>
          </div>
        )}

        {testError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Failed to load test information. Please verify your registration link.
            </p>
          </div>
        )}

        {testNotFound && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Test not found or not active. Please verify your registration link or contact the hiring team.
            </p>
          </div>
        )}

        {test && (
          <div className="rounded-lg border bg-card p-4">
            <h3 className="font-semibold">{test.name}</h3>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium leading-none">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              readOnly
              className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground cursor-not-allowed"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium leading-none">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium leading-none">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="githubId" className="text-sm font-medium leading-none">
              GitHub Username
            </label>
            <div className="relative">
              <input
                id="githubId"
                type="text"
                value={githubId}
                onChange={(e) => {
                  setGithubId(e.target.value);
                  if (githubStatus !== 'idle') {
                    setGithubStatus('idle');
                    setGithubAvatarUrl(null);
                  }
                }}
                onBlur={handleGithubBlur}
                placeholder="Enter your GitHub username"
                required
                className={cn(
                  "flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  githubStatus === 'valid' && "border-green-500",
                  githubStatus === 'invalid' && "border-destructive"
                )}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {githubStatus === 'loading' && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
                {githubStatus === 'valid' && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
                {githubStatus === 'invalid' && (
                  <XCircle className="h-4 w-4 text-destructive" />
                )}
              </div>
            </div>
            {githubStatus === 'valid' && githubAvatarUrl && (
              <div className="flex items-center gap-2 mt-2">
                <img
                  src={githubAvatarUrl}
                  alt={`${githubId}'s GitHub avatar`}
                  className="h-6 w-6 rounded-full"
                />
                <span className="text-sm text-green-600">GitHub user found</span>
              </div>
            )}
            {githubStatus === 'invalid' && (
              <p className="text-sm text-destructive mt-1">
                GitHub username not found. Please check and try again.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Your GitHub username will be verified. Actual verification happens server-side.
            </p>
          </div>

          {formError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">{formError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={registerLoading || !firstName.trim() || !lastName.trim() || !githubId.trim() || !test}
            className={cn(
              "inline-flex items-center justify-center w-full h-10 px-4 py-2 rounded-md text-sm font-medium",
              "bg-primary text-primary-foreground hover:bg-primary/90",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
          >
            {registerLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registering...
              </>
            ) : (
              'Complete Registration'
            )}
          </button>
        </form>
      </div>
    </PageLayout>
  );
}
